import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Loader2,
    Send,
    CheckCircle2,
    ChevronDown,
    ListChecks,
} from "lucide-react";
import { api } from "../lib/api";
import { SERVICE_CATEGORIES } from "../lib/services";
import FadeUp from "../components/FadeUp";

const STATUSES = ["yes", "no", "na"];
const STATUS_LABEL = { yes: "Yes", no: "No", na: "N/A" };

export default function ProjectChecklist() {
    const [contact, setContact] = useState({
        name: "",
        company: "",
        phone: "",
        email: "",
        notes: "",
        timeline: "",
        honeypot: "",
    });
    const [openCategories, setOpenCategories] = useState({});
    const [items, setItems] = useState({}); // { itemKey: 'yes'|'no'|'na' }
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");

    const updateContact = (k) => (e) =>
        setContact((c) => ({ ...c, [k]: e.target.value }));

    const toggleCategory = (id) =>
        setOpenCategories((o) => ({ ...o, [id]: !o[id] }));

    const setItemStatus = (key, value) =>
        setItems((prev) => ({ ...prev, [key]: value }));

    const yesCount = Object.values(items).filter((v) => v === "yes").length;

    const submit = async (e) => {
        e.preventDefault();
        setStatus("loading");
        setError("");
        const selections = [];
        SERVICE_CATEGORIES.forEach((cat) => {
            if (!openCategories[cat.id]) return;
            cat.groups.forEach((g) => {
                g.items.forEach((it) => {
                    const key = `${cat.id}::${g.title}::${it}`;
                    const v = items[key];
                    if (v) {
                        selections.push({
                            category: cat.title,
                            group: g.title,
                            item: it,
                            status: v,
                        });
                    }
                });
            });
        });
        try {
            await api.post("/service-inquiry", {
                name: contact.name,
                company: contact.company,
                phone: contact.phone,
                email: contact.email,
                timeline: contact.timeline,
                notes: contact.notes,
                selections,
                honeypot: contact.honeypot,
            });
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setError(
                err?.response?.data?.detail ||
                    "Something went wrong. Please try again or call us.",
            );
        }
    };

    if (status === "success") {
        return (
            <main
                data-testid="project-checklist-page"
                className="bg-[#050505] min-h-[100svh] pt-40 pb-24"
            >
                <div className="container-bevel max-w-3xl">
                    <div
                        data-testid="checklist-success"
                        className="border border-[#F5A623]/40 bg-[#0F0F0F] p-12 text-center"
                    >
                        <CheckCircle2
                            className="mx-auto text-[#F5A623]"
                            size={48}
                        />
                        <h2 className="font-display text-3xl md:text-4xl mt-6 text-white">
                            Checklist Received
                        </h2>
                        <p className="text-white/60 mt-4 max-w-xl mx-auto leading-relaxed">
                            Thanks for the detailed scope. A Bevel project
                            manager will review and reach out within 24 hours
                            to schedule a walkthrough or consult.
                        </p>
                        <Link to="/" className="btn-secondary mt-8 inline-flex">
                            Back to Home
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main
            data-testid="project-checklist-page"
            className="bg-[#050505] pt-32 pb-24"
        >
            <div className="container-bevel">
                {/* HEADER */}
                <FadeUp>
                    <span className="overline">Project Checklist</span>
                    <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.0] max-w-4xl">
                        Tell us exactly{" "}
                        <span className="text-[#F5A623]">what you need.</span>
                    </h1>
                    <p className="text-white/60 mt-6 max-w-2xl leading-relaxed">
                        Mirrors our full Construction & Contracting Services
                        intake. Pick the categories that apply, then mark each
                        line item Yes / No / N/A. We'll respond with a scoped
                        consult within 24 hours.
                    </p>
                </FadeUp>

                <form
                    onSubmit={submit}
                    className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10"
                    data-testid="project-checklist-form"
                >
                    {/* LEFT: contact */}
                    <FadeUp className="lg:col-span-4 lg:sticky lg:top-28 self-start">
                        <div className="border border-white/10 bg-[#0A0A0A] p-8">
                            <span className="overline">Your Info</span>
                            {/* Honeypot */}
                            <input
                                type="text"
                                value={contact.honeypot}
                                onChange={updateContact("honeypot")}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                className="hidden"
                                name="company-bot"
                            />
                            <label className="block mt-5">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                                    Name *
                                </span>
                                <input
                                    required
                                    value={contact.name}
                                    onChange={updateContact("name")}
                                    className="input-bevel mt-2"
                                    data-testid="checklist-name"
                                />
                            </label>
                            <label className="block mt-4">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                                    Company
                                </span>
                                <input
                                    value={contact.company}
                                    onChange={updateContact("company")}
                                    className="input-bevel mt-2"
                                    data-testid="checklist-company"
                                />
                            </label>
                            <label className="block mt-4">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                                    Phone *
                                </span>
                                <input
                                    type="tel"
                                    required
                                    value={contact.phone}
                                    onChange={updateContact("phone")}
                                    className="input-bevel mt-2"
                                    data-testid="checklist-phone"
                                />
                            </label>
                            <label className="block mt-4">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                                    Email *
                                </span>
                                <input
                                    type="email"
                                    required
                                    value={contact.email}
                                    onChange={updateContact("email")}
                                    className="input-bevel mt-2"
                                    data-testid="checklist-email"
                                />
                            </label>
                            <label className="block mt-4">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                                    Estimated Timeline
                                </span>
                                <input
                                    value={contact.timeline}
                                    onChange={updateContact("timeline")}
                                    placeholder="e.g. 4–6 weeks, ASAP, Q3"
                                    className="input-bevel mt-2"
                                    data-testid="checklist-timeline"
                                />
                            </label>
                            <label className="block mt-4">
                                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55">
                                    Project Goals / Notes
                                </span>
                                <textarea
                                    rows={3}
                                    value={contact.notes}
                                    onChange={updateContact("notes")}
                                    className="input-bevel mt-2 resize-none"
                                    data-testid="checklist-notes"
                                />
                            </label>

                            <div className="mt-6 pt-5 border-t border-white/10">
                                <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em]">
                                    <span className="text-white/50">
                                        Items selected
                                    </span>
                                    <span
                                        className="text-[#F5A623] font-semibold"
                                        data-testid="checklist-yes-count"
                                    >
                                        {yesCount}
                                    </span>
                                </div>
                            </div>

                            {status === "error" && (
                                <div
                                    data-testid="checklist-error"
                                    className="mt-4 text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={status === "loading"}
                                data-testid="checklist-submit"
                                className="btn-primary w-full mt-6 justify-center disabled:opacity-60"
                            >
                                {status === "loading" ? (
                                    <>
                                        <Loader2
                                            className="animate-spin"
                                            size={16}
                                        />{" "}
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send size={14} /> Send Checklist
                                    </>
                                )}
                            </button>
                        </div>
                    </FadeUp>

                    {/* RIGHT: categories */}
                    <div className="lg:col-span-8 space-y-4">
                        <FadeUp>
                            <span className="overline">Service Categories</span>
                            <p className="text-white/55 text-sm mt-3">
                                Select the categories that apply, sub-items
                                will appear with Yes / No / N/A controls.
                            </p>
                        </FadeUp>

                        {SERVICE_CATEGORIES.map((cat, ci) => {
                            const open = !!openCategories[cat.id];
                            return (
                                <FadeUp
                                    key={cat.id}
                                    delay={ci}
                                    className="border border-white/10 bg-[#0A0A0A]"
                                    data-testid={`checklist-category-${cat.id}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleCategory(cat.id)}
                                        data-testid={`checklist-toggle-${cat.id}`}
                                        className="w-full flex items-center justify-between gap-4 p-6 md:p-7 text-left hover:bg-[#101010] transition-colors"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div
                                                className={`w-5 h-5 border ${
                                                    open
                                                        ? "bg-[#F5A623] border-[#F5A623]"
                                                        : "border-white/30"
                                                } flex items-center justify-center mt-1 shrink-0`}
                                            >
                                                {open && (
                                                    <ListChecks
                                                        size={12}
                                                        className="text-black"
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <span className="overline">
                                                    {cat.eyebrow}
                                                </span>
                                                <h3 className="font-display text-xl md:text-2xl text-white mt-2 leading-tight">
                                                    {cat.title}
                                                </h3>
                                                <p className="text-white/50 text-sm mt-2">
                                                    {cat.summary}
                                                </p>
                                            </div>
                                        </div>
                                        <ChevronDown
                                            size={20}
                                            className={`text-white/50 transition-transform shrink-0 ${
                                                open ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>

                                    <AnimatePresence initial={false}>
                                        {open && (
                                            <motion.div
                                                key="content"
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{
                                                    height: "auto",
                                                    opacity: 1,
                                                }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{
                                                    duration: 0.3,
                                                    ease: [0.22, 1, 0.36, 1],
                                                }}
                                                className="overflow-hidden border-t border-white/10"
                                            >
                                                <div className="p-6 md:p-7 space-y-6">
                                                {cat.groups.map((g, gi) => (
                                                        <div key={g.title}>
                                                            <div className="flex items-center gap-2 text-[#F5A623]">
                                                                <g.icon size={14} />
                                                                <span className="text-[11px] uppercase tracking-[0.2em]">
                                                                    {g.title}
                                                                </span>
                                                            </div>
                                                            <ul className="mt-4 border border-white/10">
                                                                {g.items.map(
                                                                    (it, ii) => {
                                                                        const key = `${cat.id}::${g.title}::${it}`;
                                                                        return (
                                                                            <li
                                                                                key={key}
                                                                                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 ${
                                                                                    ii !==
                                                                                    g
                                                                                        .items
                                                                                        .length -
                                                                                        1
                                                                                        ? "border-b border-white/5"
                                                                                        : ""
                                                                                }`}
                                                                            >
                                                                                <span className="text-sm text-white/80">
                                                                                    {it}
                                                                                </span>
                                                                                <div className="flex gap-1">
                                                                                    {STATUSES.map(
                                                                                        (s) => (
                                                                                            <button
                                                                                                type="button"
                                                                                                key={s}
                                                                                                onClick={() =>
                                                                                                    setItems(
                                                                                                        (
                                                                                                            prev,
                                                                                                        ) => ({
                                                                                                            ...prev,
                                                                                                            [key]:
                                                                                                                prev[
                                                                                                                    key
                                                                                                                ] ===
                                                                                                                s
                                                                                                                    ? null
                                                                                                                    : s,
                                                                                                        }),
                                                                                                    )
                                                                                                }
                                                                                                data-testid={`checklist-item-${cat.id}-${gi}-${ii}-${s}`}
                                                                                                className={`px-3 py-1 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                                                                                                    items[
                                                                                                        key
                                                                                                    ] ===
                                                                                                    s
                                                                                                        ? s ===
                                                                                                          "yes"
                                                                                                            ? "bg-[#F5A623] border-[#F5A623] text-black"
                                                                                                            : s ===
                                                                                                                "no"
                                                                                                              ? "bg-white/10 border-white/30 text-white"
                                                                                                              : "bg-white/5 border-white/20 text-white/60"
                                                                                                        : "border-white/15 text-white/55 hover:border-white/40 hover:text-white"
                                                                                                }`}
                                                                                            >
                                                                                                {
                                                                                                    STATUS_LABEL[
                                                                                                        s
                                                                                                    ]
                                                                                                }
                                                                                            </button>
                                                                                        ),
                                                                                    )}
                                                                                </div>
                                                                            </li>
                                                                        );
                                                                    },
                                                                )}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </FadeUp>
                            );
                        })}
                    </div>
                </form>
            </div>
        </main>
    );
}
